/**
 * @module cercaliagl/MapPois
 */
import { createUid } from './util';
import Marker from './Marker';
import LonLat from './LonLat';
import Icon from './Icon';
import Popup from './Popup';
import { IMG } from './globals';
import Pois from './service/Pois';
import MarkerLayer from './layer/Marker';

/**
 * @typedef {Options} Options
 * @property {import("./Map").default} map Map.
 * @property {Array.<string>} [categories] Cercalia POI categories id list.
 * @property {Function} [createMarkersFunction] Create markers function. If is preferred to use a custom function to create markers. It is useful when you want to change default icon or default popups
 * @property {MarkerLayer} [layer] Add custom layer.
 */

/**
 * @classdesc
 *
 * <b>Javascript ES5</b>
 * ```js
 * const map = new cercaliagl.Map({ ... });
 * ```
 *
 * Class to get POIs while user is interaction with map. So, when `cercaliagl.MapPois` is enabled and has defined some POI categories,
 * automatically POIs will be drawn on the map once server responds all requests which are launched on `moveend` or `zoomend` events.
 *
 * If you need a list of public categories or you need to publish your own POIs, contact suport please.
 *
 * Example usage:
 *
 * ```js
 * const map = new cercaliagl.Map({map: map});
 * const mapPois = new cercaliagl.MapPois({
 *   map,
 *   categories: ['D00CAM', 'D00ESC'],
 * });
 * mapPois.enable();
 * ```
 *
 * @api
 */
class MapPois {
  /**
   * @constructor
   * @param {Options} options Options
   */
  constructor(options) {
    /**
     * @private
     * @type {import("./Map").default}
     */
    this.map_ = options.map;

    /**
     * @private
     * @type {Array.<string>}
     */
    this.categories_ = options.categories ? options.categories : [];

    /**
     * Custom function to create markers. This function must return an array of markers
     * @private
     * @type {Function}
     */
    this.createMarkersFunction_ = options.createMarkersFunction ? options.createMarkersFunction : null;

    /**
     * @private
     * @type {Array.<Marker>}
     */
    this.markers_ = [];

    /**
     * @private
     * @type {import("maplibre-gl").Map}
     */
    this.mbMap_ = this.map_.getMapboxglMap();

    /**
     * @private
     * @type {MarkerLayer}
     */
    this.layer_ = options.layer ? options.layer : new MarkerLayer({
      id: `MapPoisLayer${createUid()}`,
      map: this.map_,
    });

    /**
     * @private
     * @type {Pois}
     */
    this.poisService_ = new Pois({
      categories: this.categories_,
      searchByScale: true,
      width: 50,
      height: 50,
    });

    this.map_.addLayer(this.layer_);

    this.requestPoisByCurrentExtent_ = this.requestPoisByCurrentExtent_.bind(this);
  }

  /**
   * @api
   * @return {Array.<strirng>} Current active id categories.
   */
  getCategories() {
    return this.categories_;
  }

  /**
   * Append active categories
   * @api
   * @param {Array.<string>} categories
   */
  appendCategories(categories) {
    this.setCategories(this.categories_.concat(categories));
  }

  /**
   * Remove active categories.
   * @param {Array.<string>} categories Categories to remove
   * @api
   */
  removeCategories(categories) {
    this.setCategories(this.categories_.filter((cat) => categories.indexOf(cat) === -1));
  }

  /**
   * @api
   * @param {Array.<string>} categories Categories
   */
  setCategories(categories) {
    if (categories && categories.length > 0) {
      this.categories_ = categories;
      this.poisService_.setQueryCategories(this.categories_);
      this.requestPoisByCurrentExtent_();
    } else {
      this.poisService_.setQueryCategories([]);
      this.removeAllMarkers();
    }
  }

  /**
   * @api
   */
  removeAllMarkers() {
    this.layer_.clearMarkers();
    this.markers_ = [];
  }

  /**
   * @return {MarkerLayer}
   * @api
   */
  getLayer() {
    return this.layer_;
  }

  /**
   * Enable interaction
   * @api
   */
  enable() {
    this.mbMap_.on('moveend', this.requestPoisByCurrentExtent_);
    this.requestPoisByCurrentExtent_();
  }

  /**
   * Disable interaction
   * @api
   */
  disable() {
    this.removeAllMarkers();
    this.mbMap_.off('moveend', this.requestPoisByCurrentExtent_);
  }

  /**
   * @api
   */
  requestPoisByCurrentExtent_() {
    if (this.categories_.length > 0) {
      const bounds = this.map_.getBounds();
      const size = this.map_.getSize();
      this.poisService_.setBounds(bounds);
      this.poisService_.setWidth(size[0] * 2);
      this.poisService_.setHeight(size[1] * 2);
      this.poisService_.setSearchbyScale(this.map_.getZoom() <= 14);
      this.poisService_.doPoisRequest(this.handlePoisResponse_.bind(this));
    }
  }

  /**
   * @static
   * @param {import("./servers").PoisResult} response
   * @return {Array.<Object>}
   */
  static normalizePoisArray(data) {
    let jsonMarkersArray = [];
    if (data.cercalia.map.gpoicats && data.cercalia.map.gpoicats.poilist && data.cercalia.map.gpoicats.poilist.poi) { // if gpoicats (searchScale = false)
      jsonMarkersArray = Array.isArray(data.cercalia.map.gpoicats.poilist.poi) ? data.cercalia.map.gpoicats.poilist.poi : [data.cercalia.map.gpoicats.poilist.poi];
    } else if (data.cercalia.map.getpoicats && data.cercalia.map.getpoicats.poilist && data.cercalia.map.getpoicats.poilist.poi) { // if getpoicats (searchScale = false)
      jsonMarkersArray = Array.isArray(data.cercalia.map.getpoicats.poilist.poi) ? data.cercalia.map.getpoicats.poilist.poi : [data.cercalia.map.getpoicats.poilist.poi];
    }
    return jsonMarkersArray;
  }

  /**
   * @private
   * @param {import("./servers").PoisResult} data Server data response JSON.
   */
  handlePoisResponse_(data) {
    const pois = MapPois.normalizePoisArray(data);
    this.removeAllMarkers();
    const createMarkersFn = this.createMarkersFunction_ ? this.createMarkersFunction_.bind(this) : MapPois.createCercaliaMarkers;
    if (pois.length > 0 && pois[0] !== null) {
      this.markers_ = createMarkersFn(pois);
      this.map_.addMarkers(this.markers_, this.layer_);
    }
  }

  /**
   * @param {Array.<Object>} pois Pois
   */
  static createCercaliaMarkers(pois) {
    const markers = [];
    for (let i = 0; i < pois.length; i += 1) {
      const poi = pois[i];
      if (poi) {
        const poiId = poi.id;
        let imgPoiName = poi.category_id;
        // when subcategory. Filename is => '$categoryId_$subcategoryId.svg'
        if (poi.subcategory_id && poi.subcategory_id !== '-1') {
          imgPoiName += `_${poi.subcategory_id}`;
        }
        const img = `${IMG}/pois/${imgPoiName}.png`;
        const marker = new Marker(/** @type {import('./Marker').MarkerOptions} */({
          id: poiId,
          position: new LonLat(poi.coord.x, poi.coord.y),
          icon: new Icon(/** @type {import('./Icon').IconOptions} */({
            src: img,
          })),
          popup: new Popup(/** @type {import('./Popup').PopupOptions} */({
            title: poi.name.value,
            content: `<div>${poi.info.value}</div>`,
            minWidth: 360,
            offset: [0, -20],
          })),
        }));
        markers.push(marker);
      }
    }
    return markers;
  }
}

export default MapPois;
